import React, { useEffect, useRef, useState } from 'react';
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Spinner, Form } from 'react-bootstrap';
import { Check, CalendarDays } from 'lucide-react';
import CompanySetupLayout from '../components/Onboarding/CompanySetupLayout';
import SubscriptionPlanCard from '../components/Common/SubscriptionPlanCard';
import { useFeatures } from '../../context/FeatureContext';
import { useCapabilities } from '../../context/CapabilityContext';
import { getSelectedCompany } from '../../store/selectors/AuthSelectors';
import { logout } from '../../store/actions/AuthActions';
import axios from '../../services/AxiosInstance';
import { getApiUrl } from '../../utils/ApiUtils';
import { billingCycles, publishedBillingCycles } from '../../utils/billingCycles';
import InvoiceDetails, { validInvoiceDetails } from '../components/Settings/Subscription/InvoiceDetails';

export default function CompanyPlanSelection() {
    const { companyId } = useParams();
    const { entitlements, refreshEntitlements } = useFeatures();
    const { capabilities, hasLoaded: rolesLoaded, error: roleError, refreshCapabilities } = useCapabilities();
    const selectedCompany = useSelector(getSelectedCompany);
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const [plans, setPlans] = useState([]);
    const [cycle, setCycle] = useState('monthly');
    const [configured, setConfigured] = useState(false);
    const [paymentConfig, setPaymentConfig] = useState({ methods: ['card'], trialWithoutCard: true });
    const [paymentMethod, setPaymentMethod] = useState('');
    const [billingDetails, setBillingDetails] = useState({});
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState('');
    const [checking, setChecking] = useState(false);
    const [reload, setReload] = useState(0);
    const [confirmationRetry, setConfirmationRetry] = useState(0);
    const currentCompany = useRef(companyId);
    currentCompany.current = companyId;
    useEffect(() => () => { currentCompany.current = null; }, []);
    const rolesReady = rolesLoaded && capabilities?.companyId === companyId;
    const isOwner = rolesReady && capabilities.isOwner;
    const access = entitlements?.billingAccess;
    const awaitingPlan = entitlements?.planSelectionRequired || access?.code === 'BILLING_PLAN_SELECTION_REQUIRED';
    const active = access?.allowed === true && !awaitingPlan;
    const query = new URLSearchParams(location.search);
    const sessionId = query.get('payment') === 'plan_success' ? query.get('session_id') : null;
    const returning = query.get('payment') === 'plan_success';
    const trial = entitlements?.trialEndsAt && new Date(entitlements.trialEndsAt) > new Date();

    useEffect(() => {
        let current = true;
        setLoading(true); setLoadError(''); setError(''); setBusy('');
        Promise.all([axios.get(getApiUrl('plans')), axios.get(getApiUrl('subscription/stripe/config'))])
            .then(([offers, config]) => {
                if (!current) return;
                const available = offers.data.filter(plan => plan.plan_name !== 'beta_free');
                setPlans(available); setConfigured(config.data.configured === true);
                const payments = { methods: ['card'], trialWithoutCard: true, ...config.data };
                setPaymentConfig(payments);
                setPaymentMethod(trial && payments.trialWithoutCard ? '' : payments.methods[0] || '');
                const cycles = publishedBillingCycles(available);
                setCycle(previous => cycles.includes(previous) ? previous : cycles[0] || 'monthly');
            }).catch(() => { if (current) setLoadError('Kunde inte hämta prenumerationerna. Försök igen.'); })
            .finally(() => { if (current) setLoading(false); });
        return () => { current = false; };
    }, [companyId, reload, trial]);

    useEffect(() => {
        if (paymentMethod !== 'invoice') return;
        let live = true;
        axios.get(getApiUrl(`${companyId}/subscription/payment-options`)).then(({data}) => {
            if (live) setBillingDetails(data.billingDetails || {});
        }).catch(() => { if (live) setError('Kunde inte hämta fakturauppgifterna. Fyll i dem nedan.'); });
        return () => { live = false; };
    }, [companyId,paymentMethod]);

    // A return URL never grants access. The existing, owner-authorized endpoint
    // verifies this company's persisted Checkout against Stripe. Bounded retries
    // recover delayed confirmation; the catalog entitlement decides completion.
    useEffect(() => {
        if (!isOwner || !sessionId || active) return;
        let current = true, timer, attempt = 0;
        const confirm = async () => {
            setChecking(true); setError('');
            let retryable = true;
            try {
                await axios.post(getApiUrl(`${companyId}/subscription/checkout/confirm`), { sessionId });
                if (current) await refreshEntitlements();
            } catch (failure) {
                if (current) setError('Prenumerationen kunde inte bekräftas ännu. Kontrollera igen eller kontakta oss för hjälp.');
                retryable = ![400, 403, 404].includes(failure.response?.status);
            } finally {
                if (current) {
                    setChecking(false);
                    if (retryable && attempt < 3) timer = setTimeout(confirm, [2000, 5000, 10000][attempt++]);
                }
            }
        };
        confirm();
        return () => { current = false; clearTimeout(timer); };
    }, [companyId, sessionId, isOwner, active, refreshEntitlements, confirmationRetry]);

    const choose = async planName => {
        if (!isOwner || busy || !configured) return;
        setBusy(planName); setError('');
        try {
            const { data } = await axios.post(getApiUrl(`${companyId}/subscription/plan/checkout`), { planName, billingCycle: cycle,
                ...(paymentMethod ? { paymentMethod } : {}), ...(paymentMethod === 'invoice' ? { billingDetails } : {}) });
            if (currentCompany.current !== companyId) return;
            if (data.url) { window.location.assign(data.url); return; }
            // Manually invoiced customers may activate without hosted Checkout.
            await refreshEntitlements();
            if (!data.activated) setError('Aktiveringen inväntas. Kontrollera status igen innan du fortsätter.');
        } catch (failure) {
            if (currentCompany.current !== companyId) return;
            setError(failure.response?.status < 500 ? failure.response?.data?.error || 'Kunde inte öppna beställningen. Försök igen.' : 'Beställningen är tillfälligt otillgänglig. Försök igen.');
        } finally { if (currentCompany.current === companyId) setBusy(''); }
    };

    if (active) return <Redirect to={`/${companyId}/dashboard`} />;
    if (!awaitingPlan && access?.allowed === false) return <Redirect to={`/${companyId}/settings/subscription`} />;
    const blocked = access?.code !== 'BILLING_PLAN_SELECTION_REQUIRED';
    const companyName = selectedCompany?.companyId === companyId ? selectedCompany.companyName : 'Ditt företag';

    return <CompanySetupLayout companyName={companyName} onLogout={() => dispatch(logout(history))}>
        {!rolesReady ? <section className="company-setup-status" role="status">
            <h1>{roleError ? 'Kunde inte hämta din behörighet' : 'Hämtar din behörighet'}</h1>
            {roleError ? <button className="company-setup-button" onClick={refreshCapabilities}>Försök igen</button> : <Spinner animation="border" size="sm" />}
        </section> : !isOwner ? <section className="company-setup-status">
            <h1>Företaget är snart redo</h1>
            <p>Företagets ägare behöver välja en prenumeration innan ni kan börja. Be ägaren att logga in och slutföra aktiveringen.</p>
            <button className="company-setup-button" onClick={refreshEntitlements}>Kontrollera aktivering</button>
        </section> : returning ? <section className="company-setup-status" aria-live="polite">
            <h1>Vi bekräftar din prenumeration</h1>
            <p>Du kommer vidare till företaget när aktiveringen är bekräftad.</p>
            {error && <Alert variant="warning">{error}</Alert>}
            {checking && <Spinner animation="border" size="sm" />}
            {!checking && <button className="company-setup-button" onClick={() => { setConfirmationRetry(value => value + 1); if (!sessionId) refreshEntitlements(); }}>Kontrollera igen</button>}
            <p className="mt-3 mb-0"><button className="btn btn-link" onClick={() => history.replace(`/${companyId}/choose-plan`)}>Tillbaka till planerna</button></p>
        </section> : <>
            <div className="company-setup-intro">
                <h1>Välj planen för ditt företag</h1>
                <p>Börja med den plan som passar dig. När prenumerationen är klar öppnar vi ditt företag och du kan komma igång.</p>
            </div>
            {trial ? <div className="company-setup-trial"><CalendarDays size={20} aria-hidden="true" />
                <strong>Kostnadsfritt till {new Date(entitlements.trialEndsAt).toLocaleDateString('sv-SE')}</strong><span>Ingen betalning idag.</span>
            </div> : <Alert variant="info">{entitlements?.trialEndsAt ? 'Företagets kostnadsfria period har löpt ut. Välj en plan för att aktivera företaget.' : 'Välj plan och betalningsperiod. Slutbeloppet visas före beställning.'}</Alert>}
            {trial && <div className="company-setup-trial-terms">
                {paymentConfig.trialWithoutCard && <p><Check size={18} aria-hidden="true" /><span><strong>Prova utan kort</strong>Välj betalsätt senare. Vi påminner dig innan provperioden slutar.</span></p>}
                <p><Check size={18} aria-hidden="true" /><span><strong>Byt plan under provperioden</strong>Du kan ändra ditt val utan att provperiodens slutdatum ändras.</span></p>
                <p><Check size={18} aria-hidden="true" /><span><strong>Avsluta innan första betalningen</strong>Avsluta när du vill före provperiodens slut, så debiteras du inte för prenumerationen.</span></p>
                <p className="company-setup-trial-manage">Du byter eller avslutar planen i Inställningar → Prenumeration &amp; krediter.</p>
            </div>}
            {query.get('payment') === 'cancelled' && <Alert variant="info">Beställningen avbröts. Du kan välja plan och försöka igen.</Alert>}
            {blocked && <Alert variant="warning">{access?.code === 'BILLING_COMPANY_ALREADY_REGISTERED'
                ? 'Organisationsnumret är redan registrerat. Kontakta oss för hjälp att återställa åtkomsten eller reda ut registreringen.'
                : 'Företagsregistreringen behöver kontrolleras innan du kan välja plan. Kontakta oss så hjälper vi dig.'}</Alert>}
            {error && <Alert variant="danger">{error}</Alert>}
            {loading ? <div className="company-setup-status" role="status"><Spinner animation="border" size="sm" /> Hämtar prenumerationer…</div>
                : loadError ? <div className="company-setup-status"><p role="alert">{loadError}</p><button className="company-setup-button" onClick={() => setReload(value => value + 1)}>Försök igen</button></div>
                : <>
                    {!configured && <Alert variant="info">Aktivering av prenumerationer är inte tillgänglig just nu. Försök igen senare eller kontakta oss.</Alert>}
                    {publishedBillingCycles(plans).length === 0 && <Alert variant="info">Inga prenumerationer är tillgängliga ännu.</Alert>}
                    <Form.Group controlId="onboarding-payment-method" className="mb-3">
                        <Form.Label>Betalsätt{trial ? ' efter provperioden' : ''}</Form.Label>
                        <Form.Select value={paymentMethod} onChange={event => setPaymentMethod(event.target.value)} disabled={!!busy}>
                            {trial && paymentConfig.trialWithoutCard && <option value="">Välj senare – prova utan kort</option>}
                            {paymentConfig.methods.includes('card') && <option value="card">Kortbetalning</option>}
                            {paymentConfig.methods.includes('invoice') && <option value="invoice">Faktura i SEK – 14 dagar</option>}
                        </Form.Select>
                    </Form.Group>
                    {paymentMethod === 'invoice' && <InvoiceDetails value={billingDetails} onChange={setBillingDetails} disabled={!!busy} />}
                    <div className="subscription-plan-cycles" role="group" aria-label="Betalningsperiod">
                        {publishedBillingCycles(plans).map(value => <button key={value} aria-pressed={cycle === value} disabled={!!busy} onClick={() => setCycle(value)}>{billingCycles[value].label}</button>)}
                    </div>
                    <p className="subscription-plan-note">{trial ? 'Välj hur du vill betala efter provperioden.' : 'Prenumerationspriser.'} Samtliga priser är exklusive moms.</p>
                    <div className="subscription-plan-grid">
                        {plans.map(plan => <SubscriptionPlanCard key={plan.plan_id} plan={plan} billingCycle={cycle}
                            trialEndsAt={entitlements?.trialEndsAt} disabled={!configured || !!busy || blocked || (paymentMethod === 'invoice' && !validInvoiceDetails(billingDetails)) || (!trial && !paymentMethod)}
                            actionLabel={busy === plan.plan_name ? 'Öppnar Stripe…' : `Välj ${plan.display_name}`}
                            onSelect={() => choose(plan.plan_name)} />)}
                    </div>
                    <p className="company-setup-explanation">{paymentMethod === 'invoice'
                        ? `Fakturan skickas via e-post och betalas genom din bank. ${trial ? 'Den första fakturan skapas när provperioden slutar.' : 'Du har 14 dagar på dig att betala.'}`
                        : trial && !paymentMethod && paymentConfig.trialWithoutCard
                        ? 'Du bekräftar planen hos Stripe utan att lägga till ett kort. Välj betalsätt innan provperioden slutar för att fortsätta utan avbrott.'
                        : `Du slutför beställningen och lägger till ett kort hos Stripe. ${trial ? 'Första debiteringen sker när den kostnadsfria perioden tar slut.' : ''}`}</p>
                </>}
        </>}
    </CompanySetupLayout>;
}
